// <tri-tooltip> — hover/focus tooltip for the composition chips (and
// anything else that needs a "why"). Markup convention (rendered by
// Components::Ui::Tooltip):
//
//   <tri-tooltip class="relative inline-flex">
//     <span data-tooltip-trigger tabindex="0" aria-describedby="tt-…">…</span>
//     <div data-tooltip-panel id="tt-…" role="tooltip" hidden class="absolute …">…</div>
//   </tri-tooltip>
//
// This element owns visibility and placement only; the text is server-
// rendered so it's there without JS (as a title attribute fallback on the
// trigger). Opens on pointer hover after a short delay and on keyboard
// focus immediately; closes on leave/blur/Escape and before Turbo caches
// the page. Placement: above the trigger by default, flipped below when
// there's no room, and clamped so it never runs off the viewport edge —
// chips sit at the far left and right of the scan page.

const OPEN_DELAY_MS = 120;
const GAP_PX = 8;
const MARGIN_PX = 8;

class TriTooltip extends HTMLElement {
	#lifetime: AbortController | null = null;
	#trigger: HTMLElement | null = null;
	#panel: HTMLElement | null = null;
	#timer: number | null = null;

	connectedCallback() {
		this.#trigger = this.querySelector<HTMLElement>("[data-tooltip-trigger]");
		this.#panel = this.querySelector<HTMLElement>("[data-tooltip-panel]");
		if (!this.#trigger || !this.#panel) return;

		this.#lifetime?.abort();
		this.#lifetime = new AbortController();
		const { signal } = this.#lifetime;

		this.#trigger.addEventListener("pointerenter", () => this.#scheduleShow(), {
			signal,
		});
		this.#trigger.addEventListener("pointerleave", () => this.#hide(), {
			signal,
		});
		this.#trigger.addEventListener("focus", () => this.#show(), { signal });
		this.#trigger.addEventListener("blur", () => this.#hide(), { signal });
		this.#trigger.addEventListener(
			"keydown",
			(event) => {
				if (event.key === "Escape") this.#hide();
			},
			{ signal },
		);

		document.addEventListener("turbo:before-cache", () => this.#hide(), {
			signal,
		});
	}

	disconnectedCallback() {
		this.#hide();
		this.#lifetime?.abort();
	}

	#scheduleShow() {
		this.#clearTimer();
		this.#timer = window.setTimeout(() => this.#show(), OPEN_DELAY_MS);
	}

	#clearTimer() {
		if (this.#timer !== null) {
			window.clearTimeout(this.#timer);
			this.#timer = null;
		}
	}

	#show() {
		this.#clearTimer();
		if (!this.#panel || !this.#trigger) return;

		this.#panel.hidden = false;
		this.#place();
	}

	#hide() {
		this.#clearTimer();
		if (!this.#panel || this.#panel.hidden) return;

		this.#panel.hidden = true;
		this.#panel.style.removeProperty("left");
		this.#panel.style.removeProperty("top");
	}

	// Position in the element's own coordinate space (it's position:relative)
	// from viewport measurements, so the panel can be clamped to the screen.
	#place() {
		if (!this.#panel || !this.#trigger) return;

		const host = this.getBoundingClientRect();
		const trigger = this.#trigger.getBoundingClientRect();
		const panel = this.#panel.getBoundingClientRect();

		// Horizontal: centre on the trigger, then clamp to the viewport.
		let left = trigger.left + trigger.width / 2 - panel.width / 2;
		const maxLeft = window.innerWidth - panel.width - MARGIN_PX;
		left = Math.max(MARGIN_PX, Math.min(left, maxLeft));

		// Vertical: above by default, below if that would leave the viewport.
		let top = trigger.top - panel.height - GAP_PX;
		if (top < MARGIN_PX) top = trigger.bottom + GAP_PX;

		this.#panel.style.left = `${left - host.left}px`;
		this.#panel.style.top = `${top - host.top}px`;
	}
}

if (!customElements.get("tri-tooltip")) {
	customElements.define("tri-tooltip", TriTooltip);
}
